import React, { useState, useEffect, useRef, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Row, Col, Button, Spinner } from "react-bootstrap";
import auth from "../../../sitel/demon/services/auth";
import { SectionTitle, ErrorBanner } from "../../../sitel/demon/ui/atoms";
import { DemonTile } from "../../../sitel/demon/ui/molecules";
import {
  exemptionPageTitle,
  exemptionStatus,
  exemptionNameType,
} from "../../../services/exemptions";
import AppContext from "../../../services/appContext";
import {
  createUploadCacheEntry,
  createUploadDBEntry,
  getUploadedList,
} from "../../../services/api";
import uploadFileToBlob from "../../../services/manipulateAzureBlobData";
import NukeRequest from "../../../components/atoms/nukeRequest";
import AllFilesTable from "./allFilesTable";
import nextActionMatrix, {
  possibleWizardSteps,
  stepToUrl,
} from "../../../services/nextActionMatrix";

export default function Step3DocumentUpload() {
  const { exemptionType, requestId } = useParams();

  //get current request to calculate the next step
  const appData = useContext(AppContext);
  const navigate = useNavigate();
  const currentRequest = appData.userCurrentOhrequests.filter(
    (userOhrequest) => userOhrequest.ohrequest_id === requestId
  );
  const currentRequestStatus = currentRequest[0]
    ? currentRequest[0].ohrequest_status
    : exemptionStatus.draft.key;

  const stepurl =
    stepToUrl[
      nextActionMatrix(
        currentRequestStatus,
        exemptionType,
        possibleWizardSteps.fileUpload
      )
    ];

  //setup the uploading pieces
  const [blobList, setBlobList] = useState([]);
  // UI/form management
  const [uploadingProviders, setUploadingProviders] = useState(false);
  const [uploadingOthers, setUploadingOthers] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [, setInputKey] = useState(Math.random().toString(36));
  const fileMedicalProviderInputRef = useRef(null);
  const fileOtherDocInputRef = useRef(null);

  const userId = auth.getCurrentUser().userId;

  const exemptionCategory = exemptionNameType[exemptionType][0];
  /**
   * The below code is used to get the list of files uploaded for the current request
   * It runs once when the page loads and then again every time the requestId or userId changes
   */
  useEffect(() => {
    getUploadedList(
      userId,
      requestId,
      (r) => {
        setBlobList(r.data.payload.blobs);
      },
      (r) => console.error("ERROR getUploadedList:", r)
    );
  }, [requestId, userId]);

  //------------------------------ HANDLERS ---------------------------------------------------------

  // The below function gets the list of files uploaded for the current request
  const handleGetBlobList = () => {
    const blobListLength = blobList.length;
    return new Promise((resolve, reject) => {
      /**
       * The below function checks if the list of files uploaded for the current request has changed
       * It does this by checking the length of the list of files uploaded every 3 seconds
       * It keeps checking until the length of the list of files uploaded changes or until 15 seconds have passed
       */
      const interval = setInterval(() => {
        getUploadedList(
          userId,
          requestId,
          (r) => {
            if (r.data.payload.blobs.length !== blobListLength) {
              setBlobList(r.data.payload.blobs);
              clearInterval(interval);
              resolve();
            }
          },
          (r) => {
            console.error("ERROR getUploadedList:", r);
            reject(r);
          }
        );
        // Time to wait before checking if the list of files uploaded to change, which is 3 seconds
      }, 3000);

      setTimeout(() => {
        clearInterval(interval);
        reject(new Error("No new files added within the given time."));
        // Maximum time to wait for the list of files uploaded to change, which is 30 seconds
      }, 30000);
    });
  };

  const handleOnFileSelection = (event, uploadType) => {
    const file = event.target.files[0];
    const file_name = `${userId}/${requestId}/${Date.now()}_${file.name}`;
    const updatedFile = new File([file], file_name, { type: file.type });
    const tag = event.target.dataset.tag;

    if (uploadType === "providers") {
      setUploadingProviders(true);
      setUploadingOthers(false);
    } else {
      setUploadingProviders(false);
      setUploadingOthers(true);
    }

    // Reusable error handling function
    const handleError = (errorMsg) => {
      console.error(errorMsg);
      setUploadError(errorMsg);

      // Clear the file inputs
      fileMedicalProviderInputRef.current.value = null;
      fileOtherDocInputRef.current.value = null;

      setUploadingProviders(false);
      setUploadingOthers(false);

      throw new Error(errorMsg);
    };

    const createUploadCache = () =>
      new Promise((resolve, reject) =>
        createUploadCacheEntry(requestId, file_name, resolve, (r) =>
          reject(
            `createUploadCacheEntry: ERROR creating a redis entry, response: ${r}`
          )
        )
      ).catch(handleError);

    const uploadToBlob = () =>
      uploadFileToBlob(updatedFile).catch((r) =>
        handleError(
          `uploadFileToBlob: ERROR uploading a file to blob, response: ${r}`
        )
      );

    const createDBEntry = () =>
      new Promise((resolve, reject) =>
        createUploadDBEntry(requestId, file_name, tag, resolve, (r) =>
          reject(
            `createUploadDBEntry: ERROR creating a DB entry, response: ${r}`
          )
        )
      ).catch(handleError);

    const getBlobList = () =>
      handleGetBlobList(uploadType).catch((r) =>
        handleError(
          `handleGetBlobList: ERROR getting the list of uploaded files, response: ${r}`
        )
      );

    const resetInputs = () => {
      fileMedicalProviderInputRef.current.value = null;
      fileOtherDocInputRef.current.value = null;
      setInputKey(Math.random().toString(36));
      setUploadingProviders(false);
      setUploadingOthers(false);
    };

    createUploadCache()
      .then(uploadToBlob)
      .then(createDBEntry)
      .then(getBlobList)
      .then(resetInputs)
      .catch((error) => window.scrollTo({ top: 0, behavior: "smooth" }))
      .finally(() => {
        if (uploadType === "providers") setUploadingProviders(false);
        else setUploadingOthers(false);
      });
  };

  const renderUploadSpinner = () => {
    return (
      <div className="d-flex align-items-center">
        <Spinner size="sm" className="mx-2" />
        <span> Uploading...</span>
      </div>
    );
  };

  //------------------------------ EOF HANDLERS ---------------------------------------------------------

  return (
    <>
      {uploadError ? (
        <Row>
          <ErrorBanner errorText={uploadError} />
        </Row>
      ) : null}
      <SectionTitle>{exemptionPageTitle[exemptionType]}</SectionTitle>

      <DemonTile title="Upload Documents">
        {
          <DemonTile
            title={
              exemptionCategory === "Medical"
                ? "Medical Provider Certification Form and Other Supporting Documentation"
                : "Supporting Documentation"
            }
            nested
          >
            <p>
              {exemptionCategory === "Medical"
                ? "Please upload the Medical Provider Certificate form to proceed. Your exemption request approval will be delayed if this document is not included."
                : "Please upload any documentation deemed necessary for your request."}
            </p>
            <Col md="6">
              <div className="input-group">
                <input
                  className="form-control"
                  type="file"
                  accept=".jpg, .jpeg, .png, .pdf, doc, docx"
                  id="formMedicalProviderFile"
                  data-tag="providers"
                  ref={fileMedicalProviderInputRef}
                  // The below code, passes the event and the upload type to the onFileChange function when a file is selected
                  // I am passing a callback function to the onChange event of the file input field which has the event and upload type as parameters
                  onChange={(event) =>
                    handleOnFileSelection(event, "providers")
                  }
                  disabled={uploadingOthers || uploadingProviders}
                />
                {uploadingProviders && renderUploadSpinner()}
              </div>
              <small className="form-text text-muted">
                Support for a single file upload. Maximum file size 2MB.
              </small>
            </Col>
          </DemonTile>
        }
        {blobList.length ? (
          <Row className="mt-5">
            <Col>
              <h5>Uploaded Files</h5>
              <hr style={{ boxShadow: "0px 1px 6px rgba(0, 0, 0, 0.8)" }} />
              <Row>
                <Col className="p-4">
                  <AllFilesTable blobList={blobList} />
                </Col>
              </Row>
            </Col>
          </Row>
        ) : null}
        <Row className="mt-3">
          <Col>
            <div className="form-group float-end text-center">
              <NukeRequest>Cancel</NukeRequest>
              {FileUploadMandatory(
                navigate,
                blobList.length,
                exemptionType,
                stepurl,
                requestId,
                exemptionCategory
              )}
            </div>
          </Col>
        </Row>
      </DemonTile>
    </>
  );
}

/**
 *
 * @param {*} uploadedFilesCount
 * @param {*} exemptionType
 * @param {*} stepurl
 * @param {*} requestId
 * @returns
 */
function FileUploadMandatory(
  navigate,
  uploadedFilesCount,
  exemptionType,
  stepurl,
  requestId,
  exemptionCategory
) {
  if (uploadedFilesCount > 0) {
    return NextButton(navigate, exemptionType, stepurl, requestId);
  }

  //Religious exemptions do not require file uploaded
  if (exemptionCategory === "Religious") {
    return NextButton(navigate, exemptionType, stepurl, requestId);
  }

  return (
    <span style={{ color: "red" }}>
      &nbsp;&nbsp;&nbsp;You must upload a file to proceed.
    </span>
  );
}

/**
 *
 * @param {*} exemptionType
 * @param {*} stepurl
 * @param {*} requestId
 * @returns
 */
function NextButton(navigate, exemptionType, stepurl, requestId) {
  return (
    <Button
      className="m-3 ms-md-2"
      onClick={
        () => {
          if (stepurl === stepToUrl[possibleWizardSteps.dashboard]) {
            window.location.href = "/oh-requests?flag=updateSubmitSuccess"; //Full refresh
          } else {
            navigate(`/${stepurl}/${exemptionType}/${requestId}`);
          } //eof if
        } //eof closure
      }
    >
      Continue
    </Button>
  );
}
